import React, { Fragment } from 'react';
import { Helmet } from 'react-helmet';
import { Spacer, Paragraph, Heading, Table } from '@zillow/constellation';

type IpCheckApiResult = {
    clientIpHeaders?: string[];
    forwardedForHeaders?: string[];
    remoteAddr?: string;
    detectedIp?: string;
    zgIpHeader?: string;
    xForwadedFor?: string;
    connectionRemoteAddress?: string;
    socketRemoteAddres?: string;
    connectionSocketRemoteAddress?: string;
    defaultIpAddress?: string;
};

const acceptedApiResultKeys: string[] = [
    'clientIpHeaders',
    'forwardedForHeaders',
    'remoteAddr',
    'detectedIp',
    'zgIpHeader',
    'xForwadedFor',
    'connectionRemoteAddress',
    'socketRemoteAddres',
    'connectionSocketRemoteAddress',
    'defaultIpAddress',
];

const SpacedHeader = ({ children }: { children: string }): JSX.Element => (
    <Heading paddingTop={4} paddingBottom={4} level={3}>
        {children}
    </Heading>
);

const IpResultsTable = ({ ips = {}, type = '' }: { ips: IpCheckApiResult; type: string }) => {
    const ipTypes = Object.keys(ips).filter((key) => {
        if (acceptedApiResultKeys.indexOf(key) > -1) {
            return true;
        }

        return false;
    });

    if (!ipTypes) return null;

    return (
        <Table aria-label={`ip results table for ${type}`}>
            <Table.Header appearance="dark">
                <Table.Row>
                    {ipTypes.map((k) => (
                        <Table.HeaderCell key={k}>{k}</Table.HeaderCell>
                    ))}
                </Table.Row>
            </Table.Header>
            <Table.Body>
                <Table.Row>
                    {ipTypes.map((k) => (
                        <Table.Cell key={k}>{ips[k]}</Table.Cell>
                    ))}
                </Table.Row>
            </Table.Body>
        </Table>
    );
};

const IpEqualityResult = ({
    serverIpFromApi,
    nodeDetectedIp,
}: {
    serverIpFromApi: string;
    nodeDetectedIp: string;
}): JSX.Element => {
    const isMatch = serverIpFromApi === nodeDetectedIp;

    return (
        <Paragraph>
            detected ip by node server is going to be: {nodeDetectedIp}. java detected ip {serverIpFromApi} is
            {isMatch ? ' a match' : ' not a match'}
        </Paragraph>
    );
};

const IpCheckPage = ({
    nodeDetectedIp,
    serverIpsFromNode = {},
    serverIpsFromApi = {},
    browserIpsFromApi = {},
    browserIpsFromApiNoProxy = {},
}: {
    nodeDetectedIp: string;
    serverIpsFromNode: IpCheckApiResult;
    serverIpsFromApi: IpCheckApiResult;
    browserIpsFromApi: IpCheckApiResult;
    browserIpsFromApiNoProxy: IpCheckApiResult;
}): JSX.Element => {
    return (
        <Fragment>
            <Helmet>
                <meta name="robots" content="noindex" />
                <meta name="ip-from-api" content={serverIpsFromApi.detectedIp} />
                <meta name="ip-from-node" content={nodeDetectedIp} />
            </Helmet>

            <SpacedHeader>ip from node request</SpacedHeader>
            <IpResultsTable ips={serverIpsFromNode} type="server" />
            <IpEqualityResult nodeDetectedIp={nodeDetectedIp} serverIpFromApi={serverIpsFromApi.detectedIp} />

            <SpacedHeader>server-side ip from java</SpacedHeader>
            <IpResultsTable ips={serverIpsFromApi} type="server" />

            <SpacedHeader>client-side ip from java (proxy)</SpacedHeader>
            <IpResultsTable ips={browserIpsFromApi} type="client" />

            <SpacedHeader>client-side ip from java (no-proxy)</SpacedHeader>
            <IpResultsTable ips={browserIpsFromApiNoProxy} type="client-no-proxy" />
        </Fragment>
    );
};

export default IpCheckPage;
